import { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, CURRENCIES } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Trash2, Gavel, FileSignature, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./OverviewTab";

const BID_STATUSES = [
    { v: "invited", label: "Invited" },
    { v: "submitted", label: "Submitted" },
    { v: "shortlisted", label: "Shortlisted" },
    { v: "preferred", label: "Preferred" },
    { v: "declined", label: "Declined" },
    { v: "withdrawn", label: "Withdrawn" },
];

const dateOnly = (iso) => (iso ? String(iso).slice(0, 10) : "");

function LoiPanel({ deal, onSave }) {
    const [form, setForm] = useState({
        loi_received_date: dateOnly(deal.loi_received_date),
        loi_price: deal.loi_price ?? "",
        loi_exclusivity_end: dateOnly(deal.loi_exclusivity_end),
    });
    const [saving, setSaving] = useState(false);
    const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const save = async () => {
        setSaving(true);
        try {
            await onSave({
                loi_received_date: form.loi_received_date || null,
                loi_price: form.loi_price === "" ? null : parseFloat(form.loi_price),
                loi_exclusivity_end: form.loi_exclusivity_end || null,
            });
            toast.success("LOI details saved");
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message);
        } finally {
            setSaving(false);
        }
    };

    const daysLeft = form.loi_exclusivity_end
        ? Math.ceil((new Date(form.loi_exclusivity_end) - new Date()) / 86400000)
        : null;

    return (
        <div className="border border-border rounded-md bg-white p-6" data-testid="loi-panel">
            <h3 className="font-display font-bold tracking-tight flex items-center gap-2 mb-4">
                <FileSignature className="w-4 h-4 text-gold" /> Letter of Intent
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <Field label="LOI received">
                    <Input type="date" value={form.loi_received_date} onChange={setF("loi_received_date")} data-testid="loi-date-input" />
                </Field>
                <Field label={`Headline price (${deal.currency})`}>
                    <Input type="number" className="font-mono" value={form.loi_price} onChange={setF("loi_price")} data-testid="loi-price-input" />
                </Field>
                <Field label="Exclusivity ends">
                    <Input type="date" value={form.loi_exclusivity_end} onChange={setF("loi_exclusivity_end")} data-testid="loi-exclusivity-input" />
                </Field>
                <Button onClick={save} disabled={saving} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="loi-save-btn">
                    {saving ? "Saving…" : "Save LOI"}
                </Button>
            </div>
            {daysLeft !== null && (
                <p className={`text-xs mt-3 ${daysLeft < 0 ? "text-red-600" : daysLeft <= 14 ? "text-amber-600" : "text-muted-foreground"}`}>
                    {daysLeft < 0 ? `Exclusivity expired ${-daysLeft} day${daysLeft === -1 ? "" : "s"} ago` : `${daysLeft} day${daysLeft === 1 ? "" : "s"} of exclusivity remaining`}
                </p>
            )}
        </div>
    );
}

export default function OffersTab({ dealId, deal, onSaveDeal }) {
    const [bids, setBids] = useState([]);
    const [cmp, setCmp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);

    const refresh = useCallback(async () => {
        const [a, b] = await Promise.all([
            api.get(`/bids/deal/${dealId}`),
            api.get(`/bids/deal/${dealId}/comparison`),
        ]);
        setBids(a.data);
        setCmp(b.data);
        setLoading(false);
    }, [dealId]);
    useEffect(() => { refresh(); }, [refresh]);

    const updateStatus = async (id, status) => {
        try {
            await api.patch(`/bids/${id}`, { status });
            refresh();
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    };
    const del = async (id) => {
        if (!confirm("Delete this bid?")) return;
        await api.delete(`/bids/${id}`);
        refresh();
    };

    return (
        <div className="space-y-4" data-testid="offers-tab">
            <LoiPanel deal={deal} onSave={onSaveDeal} />

            {cmp && cmp.ranked.length > 0 && (
                <div className="border border-border rounded-md bg-white p-6" data-testid="bid-comparison">
                    <h3 className="font-display font-bold tracking-tight mb-3">Live bids — latest per buyer</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {cmp.ranked.map((b, i) => (
                            <div key={b.id} className={`rounded-md border p-4 ${i === 0 ? "border-gold bg-amber-50/40" : "border-border"}`}>
                                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">#{i + 1} · Round {b.round}</div>
                                <div className="font-medium mt-0.5">{b.buyer_name || "—"}</div>
                                <div className="font-mono text-xl font-bold text-navy">{fmtMoney(b.amount, b.currency)}</div>
                                {(b.cash_at_completion != null || b.deferred != null) && (
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Cash {b.cash_at_completion != null ? fmtMoney(b.cash_at_completion, b.currency) : "—"}
                                        {" · "}Deferred {b.deferred != null ? fmtMoney(b.deferred, b.currency) : "—"}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="border border-border rounded-md bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-display font-bold tracking-tight flex items-center gap-2">
                        <Gavel className="w-4 h-4 text-gold" /> Bids
                    </h3>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="record-bid-btn">
                                <Plus className="w-4 h-4 mr-1.5" /> Record bid
                            </Button>
                        </DialogTrigger>
                        <RecordBidDialog dealId={dealId} deal={deal} onCreated={() => { setOpen(false); refresh(); }} />
                    </Dialog>
                </div>
                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                ) : bids.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-md">
                        No bids yet. Record one as each buyer submits in a controlled auction.
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="bids-table">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                <th className="text-left py-2">Buyer</th>
                                <th className="text-left py-2">Round</th>
                                <th className="text-right py-2">Bid</th>
                                <th className="text-right py-2">Cash / Deferred</th>
                                <th className="text-left py-2 pl-4">Funding</th>
                                <th className="text-left py-2">Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {bids.map((b) => (
                                <tr key={b.id} className="border-b border-border/60" data-testid={`bid-row-${b.id}`}>
                                    <td className="py-3">
                                        <div className="font-medium">{b.buyer?.name || "—"}</div>
                                        {b.buyer?.company && <div className="text-xs text-muted-foreground">{b.buyer.company}</div>}
                                    </td>
                                    <td className="py-3 font-mono">{b.round}</td>
                                    <td className="py-3 text-right font-mono font-medium text-navy">{fmtMoney(b.amount, b.currency)}</td>
                                    <td className="py-3 text-right font-mono text-xs text-muted-foreground">
                                        {b.cash_at_completion != null ? fmtMoney(b.cash_at_completion, b.currency) : "—"} / {b.deferred != null ? fmtMoney(b.deferred, b.currency) : "—"}
                                    </td>
                                    <td className="py-3 pl-4 text-xs">
                                        {b.funding?.exceeds_stated_capital ? (
                                            <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3 h-3" /> Above stated capital</span>
                                        ) : b.funding?.pof_verified ? (
                                            <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="w-3 h-3" /> PoF verified</span>
                                        ) : <span className="text-muted-foreground">Unverified</span>}
                                    </td>
                                    <td className="py-3">
                                        <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                                            <SelectTrigger className="h-8 w-36" data-testid={`bid-status-${b.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {BID_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="py-3 text-right">
                                        <Button size="icon" variant="ghost" onClick={() => del(b.id)} data-testid={`bid-delete-${b.id}`}>
                                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function RecordBidDialog({ dealId, deal, onCreated }) {
    const [form, setForm] = useState({
        buyer_id: "", round: "1", amount: "", cash_at_completion: "", deferred: "",
        currency: deal.currency || "GBP", status: "submitted", conditions: "", notes: "",
    });
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const setF = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
    useEffect(() => { api.get("/contacts?type=buyer").then((r) => setContacts(r.data)); }, []);

    const num = (v) => (v === "" ? null : parseFloat(v));
    const submit = async (e) => {
        e.preventDefault();
        if (!form.buyer_id) return toast.error("Select a buyer");
        setLoading(true);
        try {
            await api.post(`/bids/deal/${dealId}`, {
                buyer_id: form.buyer_id, round: parseInt(form.round, 10) || 1,
                amount: num(form.amount) ?? 0, cash_at_completion: num(form.cash_at_completion),
                deferred: num(form.deferred), currency: form.currency, status: form.status,
                conditions: form.conditions || null, notes: form.notes || null,
            });
            toast.success("Bid recorded");
            onCreated();
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : err.message);
        } finally { setLoading(false); }
    };

    return (
        <DialogContent data-testid="record-bid-dialog">
            <DialogHeader><DialogTitle className="font-display">Record a bid</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
                <Field label="Buyer *">
                    <Select value={form.buyer_id} onValueChange={setF("buyer_id")}>
                        <SelectTrigger data-testid="bid-buyer-select"><SelectValue placeholder="Select a buyer…" /></SelectTrigger>
                        <SelectContent>
                            {contacts.length === 0 ? <SelectItem value="none" disabled>No buyers in your contacts</SelectItem>
                                : contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ""}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Field>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Round"><Input type="number" min="1" value={form.round} onChange={setF("round")} /></Field>
                    <Field label="Bid *"><Input type="number" required className="font-mono" value={form.amount} onChange={setF("amount")} data-testid="bid-amount-input" /></Field>
                    <Field label="Currency">
                        <Select value={form.currency} onValueChange={setF("currency")}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Cash at completion"><Input type="number" className="font-mono" value={form.cash_at_completion} onChange={setF("cash_at_completion")} /></Field>
                    <Field label="Deferred"><Input type="number" className="font-mono" value={form.deferred} onChange={setF("deferred")} /></Field>
                </div>
                <Field label="Status">
                    <Select value={form.status} onValueChange={setF("status")}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{BID_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                </Field>
                <Field label="Conditions"><Textarea rows={2} value={form.conditions} onChange={setF("conditions")} /></Field>
                <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={setF("notes")} /></Field>
                <Button type="submit" disabled={loading} className="w-full bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="bid-submit-btn">
                    {loading ? "Saving…" : "Record bid"}
                </Button>
            </form>
        </DialogContent>
    );
}
